import React from 'react';
import {
  HStack, Spinner, Icon, Text,
} from '@gluestack-ui/themed';
import { WandSparkles, Eye, Lock } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { EditLockReason } from '../hooks/useModuleEditorCapabilities';

interface EditLockBannerProps {
  /** Why content editing is locked. `none` renders nothing. */
  reason: EditLockReason;
  /**
   * Real workflow state for version-locked copy (pending_approval / published /
   * retired). Defaults to published when omitted so older call sites stay safe.
   */
  workflowState?: string;
}

/**
 * EditLockBanner
 * ----
 * Single banner that explains *why* the Module Editor is read-only. Driven by
 * `EditLockReason` so the reason and the read-only gate always agree (they share
 * the same source in `useModuleEditorCapabilities`).
 */
const EditLockBanner: React.FC<EditLockBannerProps> = ({ reason, workflowState }) => {
  const { t } = useTranslation();

  if (reason === 'none') return null;

  if (reason === 'rubric_generation') {
    return (
      <HStack
        testID="rubric-generation-lock-banner"
        alignItems="center"
        space="sm"
        bg="$bgBrandSubtle"
        borderLeftWidth={3}
        borderLeftColor="$brandBase"
        py="$2.5"
        px="$4"
      >
        <Spinner size="small" color="$brandBase" />
        <Icon as={WandSparkles} size="xs" color="$brandBase" />
        <Text size="sm" color="$textHeading" fontWeight="medium">
          {t('lecturer.moduleEditor.moduleEditor.messages.generateAllRubricsInProgress')}
        </Text>
      </HStack>
    );
  }

  const isNotAuthor = reason === 'not_author';
  const lockedStateKey = (
    workflowState === 'pending_approval' || workflowState === 'retired'
  )
    ? workflowState
    : 'published';
  const lockedStateLabel = t(
    `lecturer.moduleEditor.moduleEditor.versionSelector.states.${lockedStateKey}`,
  );

  return (
    <HStack
      testID={isNotAuthor ? 'mandate-readonly-banner' : 'version-locked-banner'}
      alignItems="center"
      space="sm"
      bg="$statusInfoSubtle"
      borderLeftWidth={3}
      borderLeftColor="$statusInfo"
      py="$2.5"
      px="$4"
    >
      <Icon as={isNotAuthor ? Eye : Lock} size="xs" color="$statusInfo" />
      <Text size="sm" color="$textOnInfoSubtle" fontWeight="medium">
        {t(isNotAuthor
          ? 'lecturer.moduleEditor.moduleEditor.messages.mandateReadOnlyNotice'
          : 'lecturer.moduleEditor.moduleEditor.messages.versionLockedNotice', {
          state: lockedStateLabel,
        })}
      </Text>
    </HStack>
  );
};

export default EditLockBanner;
